'use client';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEventHandler,
} from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusSelect } from '@/components/ui/status-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { columns } from '@/lib/kanban-data';
import { cn } from '@/lib/utils';
import type { KanbanFeature } from '@/types/kanban';
import { BulkStatusUpdate } from './bulk-status-update';
import { candidateStages, type CandidateStatusId } from '@/types/candidate';
import AssociationTag from '@/components/association-tag';

type CandidateTableProps = {
  features: KanbanFeature[];
  onBulkStatusChange: (ids: string[], statusId: CandidateStatusId) => void;
  onOpenCandidate?: (candidate: KanbanFeature) => void;
  onBulkEmail?: (candidates: KanbanFeature[]) => void;
  bulkEmailDisabled?: boolean;
};

type SortKey = 'name' | 'status' | 'startAt' | 'owner';
type SortDirection = 'asc' | 'desc';
type SortConfig = {
  key: SortKey;
  direction: SortDirection;
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const STATUS_COLUMN_WIDTH = 400;
const TAGS_COLUMN_MAX_WIDTH = 400;

// const STAGE_SLUG_BY_COLUMN: Record<CandidateStatusId, string> = {
//   sollicitanten: 'gesolliciteerd',
//   uitnodigingen: 'uitnodigen',
//   'gesprek-sw': 'gesprek-sw',
//   'gesprek-opdrachtgever': 'gesprek-opdrachtgever',
//   assessment: 'assessment',
//   aangenomen: 'aangenomen',
//   afgewezen: 'reject',
// };
const CandidateTable = ({
  features,
  onBulkStatusChange,
  onOpenCandidate,
  onBulkEmail,
  bulkEmailDisabled = false,
}: CandidateTableProps) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusToUpdate, setStatusToUpdate] = useState<CandidateStatusId | null>(null);
  const ignoreRowClickRef = useRef(false);
  const ignoreRowClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const columnById = useMemo(
    () =>
      columns.reduce<Record<string, (typeof columns)[number]>>(
        (acc, column) => {
          acc[column.id] = column;
          return acc;
        },
        {}
      ),
    []
  );

  const featuresById = useMemo(() => {
    return new Map(features.map((feature) => [feature.id, feature]));
  }, [features]);

  const sortedFeatures = useMemo(() => {
    if (!sortConfig) {
      return features;
    }

    const { key, direction } = sortConfig;
    const multiplier = direction === 'asc' ? 1 : -1;

    const getSortableValue = (feature: KanbanFeature): string | number => {
      switch (key) {
        case 'name':
          return feature.name;
        case 'status':
          return columnById[feature.column]?.name ?? 'Onbekend';
        case 'startAt': {
          const startAt = feature.startAt;
          return startAt instanceof Date ? startAt.getTime() : new Date(startAt).getTime();
        }
        case 'owner':
          return feature.owner?.name ?? 'Onbekend';
        default:
          return '';
      }
    };

    return [...features].sort((a, b) => {
      const aValue = getSortableValue(a);
      const bValue = getSortableValue(b);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier;
      }

      return (
        String(aValue ?? '').localeCompare(String(bValue ?? ''), 'nl', {
          sensitivity: 'base',
        }) * multiplier
      );
    });
  }, [features, sortConfig, columnById]);

  const toggleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const getAriaSort = (key: SortKey): 'ascending' | 'descending' | 'none' => {
    if (sortConfig?.key !== key) {
      return 'none';
    }

    return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortConfig?.key !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" aria-hidden />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5" aria-hidden />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" aria-hidden />
    );
  };

  const tableHeadClass =
    "px-4 py-1 text-xs tracking-wide";

  const suppressRowClick = useCallback(() => {
    ignoreRowClickRef.current = true;
    if (ignoreRowClickTimeoutRef.current) {
      clearTimeout(ignoreRowClickTimeoutRef.current);
    }
    ignoreRowClickTimeoutRef.current = setTimeout(() => {
      ignoreRowClickRef.current = false;
      ignoreRowClickTimeoutRef.current = null;
    }, 0);
  }, []);

  const visibleIds = useMemo(
    () => sortedFeatures.map((feature) => feature.id),
    [sortedFeatures]
  );

  const visibleIdSet = useMemo(() => new Set(visibleIds), [visibleIds]);

  useEffect(() => {
    if (selectedIds.length === 0 || !statusToUpdate) {
      return;
    }
    setSelectedIds((prevSelected) =>
      prevSelected.filter((id) => visibleIdSet.has(id))
    );
  }, [visibleIdSet]);

  useEffect(() => {
    if (selectedIds.length === 0 && statusToUpdate !== null) {
      setStatusToUpdate(null);
    }
  }, [selectedIds.length, statusToUpdate]);

  const selectedCount = selectedIds.length;
  const isAllVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const handleSelectToggle = (id: string, checked: boolean) => {
    setSelectedIds((prevSelected) => {
      if (checked) {
        if (prevSelected.includes(id)) {
          return prevSelected;
        }
        return [...prevSelected, id];
      }
      return prevSelected.filter((selectedId) => selectedId !== id);
    });
  };

  const handleSelectAllVisible = (checked: boolean) => {
    if (visibleIds.length === 0) {
      return;
    }

    if (!checked) {
      setSelectedIds((prevSelected) =>
        prevSelected.filter((id) => !visibleIdSet.has(id))
      );
      return;
    }

    setSelectedIds((prevSelected) => {
      const next = new Set(prevSelected);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleApplyStatusChange = () => {
    if (selectedIds.length === 0 || !statusToUpdate) {
      return;
    }

    onBulkStatusChange(selectedIds, statusToUpdate);
    setSelectedIds([]);
    setStatusToUpdate(null);
  };

  const handleStatusChange = useCallback(
    (candidateId: string, nextStatus: CandidateStatusId) => {
      suppressRowClick();
      onBulkStatusChange([candidateId], nextStatus);
    },
    [onBulkStatusChange, suppressRowClick]
  );

  const handleCandidateSelect = (feature: KanbanFeature) => {
    onOpenCandidate?.(feature);
  };

  const selectedFeatures = useMemo(() => {
    if (selectedIds.length === 0) {
      return [];
    }
    return selectedIds
      .map((id) => featuresById.get(id))
      .filter((candidate): candidate is KanbanFeature => Boolean(candidate));
  }, [featuresById, selectedIds]);

  const selectedFeaturesWithEmail = useMemo(
    () =>
      selectedFeatures.filter((feature) => {
        const email = feature.email?.trim();
        return Boolean(email);
      }),
    [selectedFeatures]
  );

  const handleBulkEmail = () => {
    if (!onBulkEmail || selectedFeaturesWithEmail.length === 0) {
      return;
    }
    onBulkEmail(selectedFeaturesWithEmail);
  };

  const makeRowKeyDownHandler =
    (feature: KanbanFeature): KeyboardEventHandler<HTMLTableRowElement> =>
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        const target = event.target as HTMLElement | null;
        if (
          target &&
          target.closest('input, button, a, [role="menuitem"], [data-slot="status-select-trigger"]')
        ) {
          return;
        }
        if (ignoreRowClickRef.current) {
          ignoreRowClickRef.current = false;
          return;
        }
        event.preventDefault();
        onOpenCandidate?.(feature);
      };

  useEffect(() => {
    return () => {
      if (ignoreRowClickTimeoutRef.current) {
        clearTimeout(ignoreRowClickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="custom-scrollbar flex h-full w-full min-h-0 flex-col overflow-hidden pb-6">
      <div className="mx-6 flex-1 overflow-auto">
        {selectedCount > 0 ? (
          <div className="mb-3 flex justify-end">
            <span className="text-sm text-slate-500">
              {selectedCount}{' '}
              {selectedCount === 1
                ? 'kandidaat geselecteerd'
                : 'kandidaten geselecteerd'}
            </span>
          </div>
        ) : null}
        <div className="overflow-hidden">
          <Table className="min-w-full">
            <TableHeader className='border-none text-zinc-500'>
              <TableRow className='border-slate-200'>
                <TableHead className="w-12 px-4 py-1">
                  <input
                    aria-label="Selecteer zichtbare kandidaten"
                    className="h-4 w-4"
                    type="checkbox"
                    disabled={sortedFeatures.length === 0}
                    checked={isAllVisibleSelected}
                    onChange={(event) => handleSelectAllVisible(event.target.checked)}
                    ref={(checkbox) => {
                      if (checkbox) {
                        checkbox.indeterminate =
                          selectedCount > 0 && !isAllVisibleSelected;
                      }
                    }}
                  />
                </TableHead>
                <TableHead
                  aria-sort={getAriaSort('name')}
                  className={tableHeadClass}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('name')}
                    className="px-0! flex items-center w-full flex-1 flex-row justify-between"
                  >
                    <span>Kandidaat</span>
                    {renderSortIcon('name')}
                  </Button>
                </TableHead>
                <TableHead
                  aria-sort={getAriaSort('status')}
                  className={tableHeadClass}
                  style={{ width: STATUS_COLUMN_WIDTH }}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('status')}
                    className="px-0! flex items-center w-full flex-1 flex-row justify-between"
                  >
                    <span>Status</span>
                    {renderSortIcon('status')}
                  </Button>
                </TableHead>
                <TableHead
                  aria-sort={getAriaSort('startAt')}
                  className={tableHeadClass}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('startAt')}
                    className="px-0! flex items-center w-full flex-1 flex-row justify-between"
                  >
                    <span>Sollicitatiedatum</span>
                    {renderSortIcon('startAt')}
                  </Button>
                </TableHead>
                <TableHead
                  aria-sort={getAriaSort('owner')}
                  className={tableHeadClass}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('owner')}
                    className="px-0! flex items-center w-full flex-1 flex-row justify-between"
                  >
                    <span>Eigenaar</span>
                    {renderSortIcon('owner')}
                  </Button>
                </TableHead>
                <TableHead
                  className={`${tableHeadClass} max-w-[400px]`}
                  style={{ width: TAGS_COLUMN_MAX_WIDTH }}
                >
                  Tags
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-sm">
              {sortedFeatures.length === 0 ? (
                <TableRow>
                  <TableCell className="px-4 py-6 text-center text-slate-500" colSpan={6}>
                    Geen kandidaten beschikbaar.
                  </TableCell>
                </TableRow>
              ) : (
                sortedFeatures.map((feature) => {
                  const manualTags = feature.associationTags ?? [];

                  return (
                    <TableRow
                      key={feature.id}
                      className={cn(
                        'hover:bg-primary-bg/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 border-slate-200',
                        'cursor-pointer transition-colors'
                      )}
                      onClick={(event) => {
                        if (ignoreRowClickRef.current) {
                          ignoreRowClickRef.current = false;
                          event.stopPropagation();
                          return;
                        }
                        handleCandidateSelect(feature);
                      }}
                      onKeyDown={makeRowKeyDownHandler(feature)}
                      role="button"
                      tabIndex={0}
                    >
                      <TableCell className="px-4 py-6">
                        <input
                          aria-label={`Selecteer ${feature.name}`}
                          className="h-4 w-4"
                          type="checkbox"
                          checked={selectedIds.includes(feature.id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) =>
                            handleSelectToggle(feature.id, event.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell className="px-4 py-6 font-medium">{feature.name}</TableCell>
                      <TableCell className="px-4 py-6" style={{ width: STATUS_COLUMN_WIDTH }}>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-4">
                            <StatusSelect
                              className="w-fit"
                              value={feature.column}
                              options={candidateStages}
                              onChange={(nextStatus) => {
                                if (feature.column === nextStatus) {
                                  return;
                                }
                                handleStatusChange(feature.id, nextStatus);
                              }}
                              triggerProps={{
                                'data-prevent-row-click': 'true',
                                onClick: (event) => {
                                  event.stopPropagation();
                                  suppressRowClick();
                                },
                                onPointerDown: (event) => {
                                  event.stopPropagation();
                                  suppressRowClick();
                                },
                                onMouseDown: (event) => {
                                  event.stopPropagation();
                                  suppressRowClick();
                                },
                                onKeyDown: (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.stopPropagation();
                                    suppressRowClick();
                                  }
                                },
                                'aria-label': `Wijzig status van ${feature.name}`,
                              }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-6 text-slate-500">
                        {dateFormatter.format(feature.startAt)}
                      </TableCell>
                      <TableCell className="px-4 py-6 text-slate-500">
                        {feature.owner?.name ?? 'Onbekend'}
                      </TableCell>
                      <TableCell
                        className="px-4 py-6 max-w-[400px]"
                        style={{ width: TAGS_COLUMN_MAX_WIDTH }}
                      >
                        {manualTags.length ? (
                          <div className="flex flex-wrap gap-1">
                            {manualTags.map((tag) =>
                              tag.label ? (
                                <AssociationTag
                                  key={`${feature.id}-${tag.slug ?? tag.label}`}
                                  label={tag.label}
                                />
                              ) : null
                            )}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <BulkStatusUpdate
        columns={columns}
        statusToUpdate={statusToUpdate}
        onStatusChange={setStatusToUpdate}
        onApply={handleApplyStatusChange}
        selectedCount={selectedCount}
        onClearSelection={() => {
          setSelectedIds([]);
          setStatusToUpdate(null);
        }}
        onEmailSend={onBulkEmail ? handleBulkEmail : undefined}
        emailDisabled={
          selectedFeaturesWithEmail.length === 0 || bulkEmailDisabled
        }
      />
    </div>
  );
};

export default CandidateTable;
